export interface FarkleSettings {
  winningScore: number;
  score1: number;
  score5: number;
  score3x1: number;
  score3x2: number;
  score3x3: number;
  score3x4: number;
  score3x5: number;
  score3x6: number;
  straight: number;
  smallStraight: number;
  threePairs: number;
  twoTriples: number;
  openingThreshold: number;
}

export type PlayerId = string;

export interface PlayerStats {
  mostHotDice: Record<PlayerId, number>;
  farkleCounts: Record<PlayerId, number>;
  farkleStreaks: Record<PlayerId, number>;
  bestHand: number;
}

export type FarkleRollOutcome = 'scoring' | 'farkle';

export interface FarkleRollEvent {
  player: PlayerId;
  dice: number[];
  outcome: FarkleRollOutcome;
  endedTurn: boolean;
  timestamp: number;
}

export interface FarkleSnapshot {
  players: PlayerId[];
  scores: Record<PlayerId, number>;
  turn: number;
  tentativeScore: number;
  currentDice: number;
  keptDice: number[];
  rolledDice: number[];
  hotDice: boolean;
  mustPick: boolean;
  lastRound: boolean;
  finalRoundPlayers: PlayerId[];
  gameOver: boolean;
  winner: PlayerId | null;
  stats: PlayerStats;
  lastRollEvent?: FarkleRollEvent | null;
}

export type FarklePhase = 'idle' | 'active' | 'final-round' | 'complete';

export interface FarkleStateDocument {
  partyId: string;
  gameId: string;
  phase: FarklePhase;
  createdAt: number;
  updatedAt: number;
  settings: FarkleSettings;
  state: FarkleSnapshot;
  computerDice: boolean;
}

export type FarkleActionType = 'start' | 'roll' | 'select' | 'bank' | 'end' | 'forfeit';

export interface FarkleActionPayloadMap {
  start: { players: PlayerId[]; settings?: Partial<FarkleSettings>; computerDice?: boolean };
  roll: { roller: PlayerId; dice?: number[] };
  select: { selector: PlayerId; indices: number[] };
  bank: { banker: PlayerId };
  end: { requester: PlayerId };
  forfeit: { player: PlayerId };
}

export type FarkleAction<T extends FarkleActionType = FarkleActionType> = {
  type: T;
  partyId: string;
  gameId: string;
  createdAt: number;
  actorId: PlayerId;
  payload: FarkleActionPayloadMap[T];
};

export class FarkleGame {
  static DEFAULT_SETTINGS: FarkleSettings = {
    winningScore: 10000,
    score1: 100,
    score5: 50,
    score3x1: 1000,
    score3x2: 200,
    score3x3: 300,
    score3x4: 400,
    score3x5: 500,
    score3x6: 600,
    straight: 2000,
    smallStraight: 1000,
    threePairs: 1500,
    twoTriples: 2500,
    openingThreshold: 0,
  };

  readonly players: PlayerId[];
  readonly scores: Record<PlayerId, number>;
  turn = 0;
  tentativeScore = 0;
  currentDice = 6;
  keptDice: number[] = [];
  rolledDice: number[] = [];
  gameOver = false;
  lastRound = false;
  finalRoundPlayers: Set<PlayerId> = new Set();
  hotDice = false;
  mustPick = false;
  bestHand = 0;
  statsMostHotDice: Record<PlayerId, number>;
  statsFarkleCounts: Record<PlayerId, number>;
  statsFarkleStreaks: Record<PlayerId, number>;
  winner: PlayerId | null = null;
  lastRollEvent: FarkleRollEvent | null = null;
  readonly settings: FarkleSettings;
  private readonly winningScore: number;
  private readonly openingThreshold: number;

  constructor(players: PlayerId[], settings?: Partial<FarkleSettings>) {
    if (!players.length) {
      throw new Error('FarkleGame requires at least one player');
    }
    this.players = [...players];
    this.scores = players.reduce<Record<PlayerId, number>>((acc, player) => {
      acc[player] = 0;
      return acc;
    }, {});
    this.statsMostHotDice = players.reduce<Record<PlayerId, number>>((acc, player) => {
      acc[player] = 0;
      return acc;
    }, {});
    this.statsFarkleCounts = players.reduce<Record<PlayerId, number>>((acc, player) => {
      acc[player] = 0;
      return acc;
    }, {});
    this.statsFarkleStreaks = players.reduce<Record<PlayerId, number>>((acc, player) => {
      acc[player] = 0;
      return acc;
    }, {});

    this.settings = { ...FarkleGame.DEFAULT_SETTINGS, ...settings };
    this.winningScore = this.settings.winningScore;
    this.openingThreshold = this.settings.openingThreshold;
  }

  static fromSnapshot(snapshot: FarkleSnapshot, settings?: Partial<FarkleSettings>): FarkleGame {
    const game = new FarkleGame(snapshot.players, settings);
    Object.assign(game.scores, snapshot.scores);
    game.turn = snapshot.turn;
    game.tentativeScore = snapshot.tentativeScore;
    game.currentDice = snapshot.currentDice;
    game.keptDice = [...snapshot.keptDice];
    game.rolledDice = [...snapshot.rolledDice];
    game.hotDice = snapshot.hotDice;
    game.mustPick = snapshot.mustPick;
    game.lastRound = snapshot.lastRound;
    game.finalRoundPlayers = new Set(snapshot.finalRoundPlayers);
    game.gameOver = snapshot.gameOver;
    game.winner = snapshot.winner;
    game.statsMostHotDice = { ...snapshot.stats.mostHotDice };
    game.statsFarkleCounts = { ...snapshot.stats.farkleCounts };
    game.statsFarkleStreaks = { ...snapshot.stats.farkleStreaks };
    game.bestHand = snapshot.stats.bestHand;
    game.lastRollEvent = snapshot.lastRollEvent ?? null;
    return game;
  }

  nextPlayer(): void {
    this.turn = (this.turn + 1) % this.players.length;
    this.tentativeScore = 0;
    this.currentDice = 6;
    this.keptDice = [];
    this.rolledDice = [];
    this.hotDice = false;
    this.mustPick = false;
  }

  getCurrentPlayer(): PlayerId {
    return this.players[this.turn];
  }

  addScore(player: PlayerId, score: number): void {
    this.scores[player] += score;
    if (this.scores[player] >= this.winningScore && !this.lastRound) {
      this.lastRound = true;
      this.finalRoundPlayers = new Set(this.players);
      this.finalRoundPlayers.delete(player);
      this.winner = player;
    }
  }

  isFinalRound(): boolean {
    return this.lastRound;
  }

  isGameOver(): boolean {
    return this.gameOver;
  }

  getWinner(): PlayerId[] | null {
    if (!this.gameOver) {
      return null;
    }
    const maxScore = Math.max(...Object.values(this.scores));
    return Object.entries(this.scores)
      .filter(([, score]) => score === maxScore)
      .map(([player]) => player);
  }

  endTurn(farkled = false): void {
    const player = this.getCurrentPlayer();
    if (farkled) {
      this.tentativeScore = 0;
      this.statsFarkleCounts[player] += 1;
      this.statsFarkleStreaks[player] += 1;
      if (this.statsFarkleStreaks[player] === 3) {
        this.scores[player] = Math.max(0, this.scores[player] - 1000);
        this.statsFarkleStreaks[player] = 0;
      }
    } else {
      this.statsFarkleStreaks[player] = 0;
    }

    if (this.lastRound && this.finalRoundPlayers.has(player)) {
      this.finalRoundPlayers.delete(player);
      if (this.finalRoundPlayers.size === 0) {
        this.gameOver = true;
      }
    }

    if (!this.gameOver) {
      this.nextPlayer();
    }
  }

  roll(randomFn: () => number = Math.random): number[] {
    if (this.mustPick && this.rolledDice.length) {
      return [...this.rolledDice];
    }
    const player = this.getCurrentPlayer();
    this.rolledDice = Array.from({ length: this.currentDice }, () => 1 + Math.floor(randomFn() * 6));
    this.mustPick = true;
    const rollResult = [...this.rolledDice];
    const outcomeScore = this.scoreRoll(this.rolledDice);
    const outcome: FarkleRollOutcome = outcomeScore === 0 ? 'farkle' : 'scoring';
    this.lastRollEvent = {
      player,
      dice: [...rollResult],
      outcome,
      endedTurn: outcome === 'farkle',
      timestamp: Date.now(),
    };
    if (outcome === 'farkle') {
      this.endTurn(true);
      return rollResult;
    }
    return rollResult;
  }

  manualRoll(values: number[]): number[] {
    if (!values.length) {
      throw new Error('Manual roll requires at least one die value');
    }
    if (values.some((v) => v < 1 || v > 6)) {
      throw new Error('Die values must be between 1 and 6');
    }
    this.rolledDice = [...values];
    this.currentDice = values.length;
    this.mustPick = true;
    return [...this.rolledDice];
  }

  selectDice(indices: number[]): { tentativeScore: number; hotDice: boolean } | { error: string } {
    if (!this.rolledDice.length) {
      return { error: 'No roll to select from.' };
    }
    const selected = indices.map((idx) => this.rolledDice[idx]).filter((value) => typeof value === 'number');
    if (!selected.length) {
      return { error: 'No dice selected.' };
    }
    const score = this.scoreRoll(selected);
    if (score === 0) {
      return { error: 'Selected dice do not score.' };
    }

    // Validate that ALL selected dice contribute to the score
    // Check if removing any single die changes the score
    for (let i = 0; i < selected.length; i++) {
      const withoutOne = selected.filter((_, idx) => idx !== i);
      const scoreWithoutOne = this.scoreRoll(withoutOne);
      
      // If score is the same without this die, it's not contributing
      if (scoreWithoutOne === score) {
        return { error: 'All selected dice must contribute to the score. You cannot keep non-scoring dice.' };
      }
    }

    this.tentativeScore += score;
    this.keptDice.push(...selected);
    if (score > this.bestHand) {
      this.bestHand = score;
    }

    const selectedSet = new Set(indices);
    this.rolledDice = this.rolledDice.filter((_, idx) => !selectedSet.has(idx));
    this.currentDice = this.rolledDice.length;
    const player = this.getCurrentPlayer();

    if (this.currentDice === 0) {
      this.hotDice = true;
      this.currentDice = 6;
      this.keptDice = [];
      this.statsMostHotDice[player] += 1;
    } else {
      this.hotDice = false;
    }

    this.mustPick = false;
    return { tentativeScore: this.tentativeScore, hotDice: this.hotDice };
  }

  farkleCheck(): boolean {
    return this.scoreRoll(this.rolledDice) === 0;
  }

  bank(): void {
    if (this.mustPick) {
      const remainingScore = this.scoreRoll(this.rolledDice);
      if (remainingScore === 0) {
        this.tentativeScore = 0;
        this.endTurn(true);
        return;
      }
      throw new Error('Select scoring dice before banking.');
    }
    const player = this.getCurrentPlayer();
    if (this.openingThreshold > 0 && this.scores[player] === 0 && this.tentativeScore < this.openingThreshold) {
      this.tentativeScore = 0;
      this.endTurn();
      return;
    }
    this.addScore(player, this.tentativeScore);
    this.endTurn();
  }

  scoreRoll(dice: number[]): number {
    if (!dice.length) {
      return 0;
    }
    const counts = dice.reduce<Record<number, number>>((acc, value) => {
      acc[value] = (acc[value] ?? 0) + 1;
      return acc;
    }, {});
    const s = this.settings;

    // Straight 1-6 (exact six dice)
    const countKeys = Object.keys(counts).map(Number).sort((a, b) => a - b);
    if (dice.length === 6 && countKeys.length === 6 && countKeys.every((val, idx) => val === idx + 1)) {
      return s.straight;
    }

    const tripleScoreFor = (num: number): number => {
      if (num === 1) return s.score3x1;
      const map: Record<number, number> = {
        2: s.score3x2,
        3: s.score3x3,
        4: s.score3x4,
        5: s.score3x5,
        6: s.score3x6,
      };
      return map[num] ?? num * 100;
    };

    // Helper to evaluate small straights
    const evaluateSmallStraight = (required: number[]): number => {
      const tempCounts: Record<number, number> = { ...counts };
      if (!required.every((val) => (tempCounts[val] ?? 0) > 0)) {
        return 0;
      }
      required.forEach((val) => {
        tempCounts[val] -= 1;
      });
      const remainingDiceCount = dice.length - required.length;
      let tempScore = 0;
      for (let num = 1; num <= 6; num += 1) {
        const count = tempCounts[num] ?? 0;
        if (count >= 3) {
          tempScore += tripleScoreFor(num) * 2 ** (count - 3);
          tempCounts[num] = 0;
        }
      }
      tempScore += (tempCounts[1] ?? 0) * s.score1;
      tempScore += (tempCounts[5] ?? 0) * s.score5;
      if (remainingDiceCount === 0 || tempScore > 0) {
        return s.smallStraight + tempScore;
      }
      return 0;
    };

    if (dice.length >= 5) {
      const small1to5 = evaluateSmallStraight([1, 2, 3, 4, 5]);
      if (small1to5) {
        return small1to5;
      }
      const small2to6 = evaluateSmallStraight([2, 3, 4, 5, 6]);
      if (small2to6) {
        return small2to6;
      }
    }

    // Three pairs
    const pairCount = Object.values(counts).filter((val) => val === 2).length;
    if (dice.length === 6 && pairCount === 3) {
      return s.threePairs;
    }

    // Two triples
    const tripleCount = Object.values(counts).filter((val) => val === 3).length;
    if (dice.length === 6 && tripleCount === 2) {
      return s.twoTriples;
    }

    let score = 0;
    for (let num = 1; num <= 6; num += 1) {
      const count = counts[num] ?? 0;
      if (count >= 3) {
        score += tripleScoreFor(num) * 2 ** (count - 3);
        counts[num] = 0;
      }
    }

    score += (counts[1] ?? 0) * s.score1;
    score += (counts[5] ?? 0) * s.score5;
    return score;
  }

  serialize(): FarkleSnapshot {
    return {
      players: [...this.players],
      scores: { ...this.scores },
      turn: this.turn,
      tentativeScore: this.tentativeScore,
      currentDice: this.currentDice,
      keptDice: [...this.keptDice],
      rolledDice: [...this.rolledDice],
      hotDice: this.hotDice,
      mustPick: this.mustPick,
      lastRound: this.lastRound,
      finalRoundPlayers: Array.from(this.finalRoundPlayers),
      gameOver: this.gameOver,
      winner: this.winner,
      stats: {
        mostHotDice: { ...this.statsMostHotDice },
        farkleCounts: { ...this.statsFarkleCounts },
        farkleStreaks: { ...this.statsFarkleStreaks },
        bestHand: this.bestHand,
      },
      lastRollEvent: this.lastRollEvent ? { ...this.lastRollEvent } : null,
    };
  }

  getPhase(): FarklePhase {
    if (this.gameOver) return 'complete';
    if (this.lastRound) return 'final-round';
    return this.rolledDice.length === 0 && this.tentativeScore === 0 ? 'idle' : 'active';
  }
}
